import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Dynamically determine backend URL
// If REACT_APP_BACKEND_URL is set, use it
// Otherwise, use current origin (for preview environments)
const getBackendURL = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Use current domain for preview environments
  return window.location.origin;
};

const API = `${getBackendURL()}/api`;

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (token) {
        try {
          // Set default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Fetch user data
          const response = await axios.get(`${API}/auth/me`);
          const userData = response.data;
          
          setUser(userData.user);
          setOrganizations(userData.organizations);
          setCurrentOrganization(userData.current_organization);
          setIsAuthenticated(true);
          
          // Store current organization in localStorage
          if (userData.current_organization) {
            localStorage.setItem('current_organization_id', userData.current_organization.id);
          }
          
        } catch (error) {
          console.error('Auth initialization failed:', error);
          
          // Try to refresh token
          if (refreshToken) {
            try {
              await refreshAuthToken();
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              logout();
            }
          } else {
            logout();
          }
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Process Google OAuth credential response
  const processGoogleLogin = async (credentialResponse) => {
    try {
      setLoading(true);
      
      // Send the credential to backend for processing
      const response = await axios.post(`${API}/auth/google/callback`, {
        code: credentialResponse.credential,
        state: 'google_oauth'
      });

      const { access_token, refresh_token } = response.data;

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Fetch user data
      const userResponse = await axios.get(`${API}/auth/me`);
      const userData = userResponse.data;

      setUser(userData.user);
      setOrganizations(userData.organizations);
      setCurrentOrganization(userData.current_organization);
      setIsAuthenticated(true);

      // Store current organization
      if (userData.current_organization) {
        localStorage.setItem('current_organization_id', userData.current_organization.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Google OAuth processing failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Login function (updated for email-only login)
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token } = response.data;

      // Store tokens temporarily
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Fetch user data
      const userResponse = await axios.get(`${API}/auth/me`);
      const userData = userResponse.data;

      // Email verification is currently disabled in product flow.
      // Complete login regardless of legacy is_verified value.
      setUser(userData.user);
      setOrganizations(userData.organizations);
      setCurrentOrganization(userData.current_organization);
      setIsAuthenticated(true);

      // Store current organization
      if (userData.current_organization) {
        localStorage.setItem('current_organization_id', userData.current_organization.id);
      }

      // Check for pending invitation token
      const pendingToken = localStorage.getItem('pendingInvitationToken');
      if (pendingToken) {
        try {
          const inviteResponse = await axios.post(`${API}/auth/accept-invitation/${pendingToken}`);
          localStorage.removeItem('pendingInvitationToken');
          
          // Refresh user data to get new organization
          const refreshedUser = await axios.get(`${API}/auth/me`);
          setOrganizations(refreshedUser.data.organizations);
          
          return { 
            success: true, 
            requiresVerification: false,
            invitationAccepted: true,
            message: inviteResponse.data.message
          };
        } catch (inviteError) {
          console.error('Failed to accept invitation:', inviteError);
          localStorage.removeItem('pendingInvitationToken');
          // Don't fail login, just log the error
        }
      }

      return { success: true, requiresVerification: false };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  // Register function (updated for first_name/last_name)
  const register = async (email, password, firstName, lastName) => {
    try {
      await axios.post(`${API}/auth/register`, {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });

      // Email verification is disabled; immediately log in after registration
      const loginResult = await login(email, password);
      if (!loginResult.success) {
        return {
          success: false,
          error: loginResult.error || 'Account created but login failed. Please log in manually.'
        };
      }

      return {
        success: true,
        requiresVerification: false,
        message: 'Account created successfully!'
      };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  // Login with provided tokens (used after email verification)
  const loginWithTokens = async (accessToken, refreshToken) => {
    try {
      console.log('loginWithTokens called with tokens:', !!accessToken, !!refreshToken);
      
      // Store tokens
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);

      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      // Fetch user data
      console.log('Fetching user data after token login...');
      const userResponse = await axios.get(`${API}/auth/me`);
      const userData = userResponse.data;
      
      console.log('User data received:', userData);

      setUser(userData.user);
      setOrganizations(userData.organizations);
      setCurrentOrganization(userData.current_organization);
      setIsAuthenticated(true);

      // Store current organization
      if (userData.current_organization) {
        localStorage.setItem('current_organization_id', userData.current_organization.id);
      }

      console.log('Authentication state updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Token-based login failed:', error);
      // Clear tokens on failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      return {
        success: false,
        error: 'Failed to complete login',
      };
    }
  };

  // Login after email verification
  const loginAfterVerification = async (email) => {
    try {
      // Fetch user data after verification
      const userResponse = await axios.get(`${API}/auth/me`);
      const userData = userResponse.data;

      setUser(userData.user);
      setOrganizations(userData.organizations);
      setCurrentOrganization(userData.current_organization);
      setIsAuthenticated(true);

      // Store current organization
      if (userData.current_organization) {
        localStorage.setItem('current_organization_id', userData.current_organization.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Post-verification login failed:', error);
      return {
        success: false,
        error: 'Failed to complete login after verification',
      };
    }
  };

  // Refresh token function
  const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token: new_refresh_token } = response.data;

      // Store new tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', new_refresh_token);

      // Update authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate server sessions
      if (isAuthenticated) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      console.error('Server logout failed:', error);
    } finally {
      // Clear local state regardless of server response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('current_organization_id');
      delete axios.defaults.headers.common['Authorization'];
      
      setUser(null);
      setCurrentOrganization(null);
      setOrganizations([]);
      setIsAuthenticated(false);
    }
  };

  // Switch organization function
  const switchOrganization = async (organizationId) => {
    try {
      const organization = organizations.find(org => org.id === organizationId);
      if (organization) {
        setCurrentOrganization(organization);
        localStorage.setItem('current_organization_id', organizationId);
        
        // Add organization context to future requests
        axios.defaults.headers.common['X-Organization-ID'] = organizationId;
        
        return { success: true };
      } else {
        return { success: false, error: 'Organization not found' };
      }
    } catch (error) {
      console.error('Organization switch failed:', error);
      return { success: false, error: 'Failed to switch organization' };
    }
  };

  // Create organization function
  const createOrganization = async (name, description = '') => {
    try {
      const response = await axios.post(`${API}/organizations`, {
        name,
        description,
      });

      const newOrganization = response.data;
      
      // Refresh user data to get updated organization list
      const userResponse = await axios.get(`${API}/auth/me`);
      const userData = userResponse.data;

      // Update state with fresh data
      setUser(userData.user);
      setOrganizations(userData.organizations);
      setCurrentOrganization(userData.current_organization);

      // Store current organization
      if (userData.current_organization) {
        localStorage.setItem('current_organization_id', userData.current_organization.id);
      }

      return { success: true, organization: newOrganization };
    } catch (error) {
      console.error('Organization creation failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create organization',
      };
    }
  };

  // Get user's role in current organization
  const getCurrentUserRole = () => {
    if (!user || !currentOrganization) return null;
    
    // This would be populated from the user context in a real implementation
    // For now, we'll assume super_admin if they're the owner
    if (currentOrganization.owner_id === user.id) {
      return 'super_admin';
    }
    
    return 'member'; // Default role
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    const role = getCurrentUserRole();
    
    // Define role permissions
    const permissions = {
      super_admin: ['manage_users', 'manage_organization', 'manage_events', 'view_analytics'],
      admin: ['manage_events', 'view_analytics'],
      manager: ['manage_events'],
      member: ['view_events'],
    };
    
    return permissions[role]?.includes(permission) || false;
  };

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Skip token refresh for auth endpoints (login, register, etc.)
        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                               originalRequest.url?.includes('/auth/register') ||
                               originalRequest.url?.includes('/auth/refresh') ||
                               originalRequest.url?.includes('/auth/forgot-password') ||
                               originalRequest.url?.includes('/auth/reset-password');
        
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;
          
          try {
            await refreshAuthToken();
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
            return axios(originalRequest);
          } catch (refreshError) {
            logout();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Get token for components that need it
  const token = localStorage.getItem('access_token');

  const value = {
    // State
    user,
    currentOrganization,
    organizations,
    loading,
    isAuthenticated,
    token, // Add token to context value
    
    // Actions
    login,
    register,
    logout,
    switchOrganization,
    createOrganization,
    processGoogleLogin,
    loginWithTokens,
    
    // Utilities
    getCurrentUserRole,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <LoginPage />;
    }

    return <Component {...props} />;
  };
};

// Protected Route component
export const ProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

// We'll create LoginPage component next
const LoginPage = () => <div>Login component will be created next</div>;